import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useSesion } from '../sesion/sesion';
import { hayBackend } from '../config/entorno';

/** Caja comun de las pantallas que se muestran cuando no hay consola que mostrar. */
function Caja({
  rotulo,
  titulo,
  children,
}: {
  rotulo: string;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <div className="ingreso">
      <div className="ingreso__caja">
        <p className="rotulo">{rotulo}</p>
        <h1 className="ingreso__titulo">{titulo}</h1>
        {children}
      </div>
    </div>
  );
}

/**
 * Ingreso en DOS pasos: correo → código que llega por correo → sesión.
 *
 * ⛔ **Antes esto era un botón que redirigía el navegador a `auth-jj`.** Cambió el 2026-09-05
 * (`docs/06` §13): la consola usa el login PROPIO de `taxi`. Lo que NO cambió es el invariante de
 * `sesion/rutas.ts` — **la consola nunca ve, guarda ni manda un token**: la sesión vive en una
 * cookie `httpOnly` y acá sólo se mandan un correo y un código.
 *
 * 🔑 **El segundo paso NO se muestra en función de si el correo existe.** El backend responde 202
 * siempre, así que esta pantalla avanza igual — si sólo avanzara para correos con acceso, la
 * pantalla sería el oráculo que el backend evita con cuidado.
 */
export function Ingreso() {
  const { pedirCodigo, ingresar } = useSesion();
  const [paso, setPaso] = useState<'correo' | 'codigo'>('correo');
  const [correo, setCorreo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      if (paso === 'correo') {
        await pedirCodigo(correo.trim());
        setPaso('codigo');
      } else {
        await ingresar(correo.trim(), codigo.trim());
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo completar el ingreso.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Caja rotulo="Consola de operación" titulo="Iniciá sesión para continuar">
      <p className="pagina__ayuda" style={{ marginTop: 0 }}>
        {paso === 'correo'
          ? 'Ingresá tu correo de trabajo. Te mandamos un código de 6 dígitos para entrar.'
          : `Si esa cuenta tiene acceso a la consola, el código llegó a ${correo.trim()}. Vence en 5 minutos.`}
      </p>

      <form onSubmit={(e) => void enviar(e)}>
        <label className="campo">
          <span className="campo__etiqueta">Correo</span>
          <input
            className="entrada"
            type="email"
            required
            autoComplete="username"
            value={correo}
            // Cambiar el correo vuelve al paso 1: un código pedido para OTRA casilla no sirve
            // acá, y dejarlo escrito invitaría a probarlo contra un correo distinto.
            onChange={(e) => {
              setCorreo(e.target.value);
              setPaso('correo');
              setCodigo('');
            }}
            disabled={!hayBackend || enviando}
          />
        </label>

        {paso === 'codigo' ? (
          <label className="campo">
            <span className="campo__etiqueta">Código</span>
            <input
              className="entrada"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              // El backend exige exactamente 6 dígitos; declararlo acá evita un viaje de ida y
              // vuelta para decir lo que el formulario ya sabe.
              minLength={6}
              maxLength={6}
              required
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              disabled={enviando}
            />
          </label>
        ) : null}

        {error ? (
          <p className="campo__ayuda" role="alert">
            {error}
          </p>
        ) : null}

        <div className="fila" style={{ marginTop: 18 }}>
          <button
            type="submit"
            className="boton boton--principal"
            disabled={!hayBackend || enviando}
          >
            {paso === 'correo' ? 'Enviarme el código' : 'Entrar'}
          </button>
        </div>
      </form>

      {hayBackend ? null : (
        <p className="campo__ayuda" style={{ marginTop: 14 }}>
          El formulario está apagado porque falta <code>VITE_API_URL</code>: el inicio de sesión lo
          maneja el backend, que es quien manda el código y quien sostiene la cookie de sesión. La
          consola nunca ve el token.
        </p>
      )}
    </Caja>
  );
}

export function SinRol({ correo }: { correo: string }) {
  const { salir } = useSesion();

  return (
    <Caja rotulo="Sin acceso" titulo="Tu cuenta no tiene rol de operación">
      <p className="pagina__ayuda" style={{ marginTop: 0 }}>
        Iniciaste sesión{' '}
        {correo ? (
          <>
            como <strong>{correo}</strong>{' '}
          </>
        ) : null}
        pero esa cuenta no está habilitada para operar. Pedile a un administrador que te asigne el
        rol de operador.
      </p>
      <div className="fila" style={{ marginTop: 18 }}>
        <button type="button" className="boton" onClick={salir}>
          Salir
        </button>
      </div>
    </Caja>
  );
}

export function SinPermiso() {
  return (
    <Caja rotulo="Sin permiso" titulo="Esta sección es de administrador">
      <p className="pagina__ayuda" style={{ marginTop: 0 }}>
        Los parámetros de dispatch, el tarifario y la auditoría los maneja un administrador: cambian
        el ingreso o la relación con el conductor. Tu turno tiene el resto de la consola.
      </p>
      <div className="fila" style={{ marginTop: 18 }}>
        <Link className="boton" to="/">
          Volver al panel
        </Link>
      </div>
    </Caja>
  );
}

export function NoEncontrado() {
  return (
    <Caja rotulo="Error 404" titulo="Esa pantalla no existe">
      <p className="pagina__ayuda" style={{ marginTop: 0 }}>
        Puede que el enlace esté viejo. Todo lo que hay está en el menú de la izquierda.
      </p>
      <div className="fila" style={{ marginTop: 18 }}>
        <Link className="boton" to="/">
          Volver al panel
        </Link>
      </div>
    </Caja>
  );
}
